import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { useGetCurrencyCodesQuery } from '@graphql/queries/currency.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { OptionType } from '@withjoy/joykit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

interface CurrencyDialogControllerArgs {
  onClose: () => void;
  eventId: Maybe<string>;
  registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  setRegistryCurrency: Maybe<(c: RegistryCurrencyCodesFragment) => void>;
}

export const useCurrencyDialogContoller = ({ onClose, eventId, registryCurrency: currentCurrency, setRegistryCurrency }: CurrencyDialogControllerArgs) => {
  const [setCurrency] = useSetRegistryCurrencyMutation();

  const { data: currencyCodeData } = useGetCurrencyCodesQuery();
  const currencyList = useMemo(() => {
    const currencyData = currencyCodeData?.getRegistryStaticData;
    const currencyListTemp = currencyData?.currencies?.topRanked.concat(currencyData?.currencies?.ordered);
    const numTopRanked = currencyData?.currencies?.topRanked.length;
    return { items: currencyListTemp, numTopRanked };
  }, [currencyCodeData]);

  const [defaultValue, setDefaultValue] = useState<OptionType>();
  const [selectedCurrency, setSelectedCurrency] = useState<OptionType>();

  useEffect(() => {
    const symbol = getCurrencySymbolFromCurrencyCode(currentCurrency?.code);
    const defaultOption = {
      value: currentCurrency?.code || DEFAULT_CURRENCY,
      label: currentCurrency?.code ? `${currentCurrency.displayName} (${symbol}, ${currentCurrency.code})` : `US Dollar (${symbol}, ${DEFAULT_CURRENCY})`,
    };
    setDefaultValue(defaultOption);
    setSelectedCurrency(defaultOption);
  }, [currentCurrency]);

  const handleOnSave = useCallback(
    (eventId: string, currency: RegistryCurrencyCodesFragment) => {
      setCurrency({
        variables: {
          eventId,
          payload: { code: currency.code },
        },
        refetchQueries: () => ['GetAdminRegistryData', 'PublicShopRoot'],
      });

      setRegistryCurrency && setRegistryCurrency(currency);
    },
    [setCurrency, setRegistryCurrency],
  );

  const currencyOptions: OptionType[] =
    currencyList?.items?.map((option: RegistryCurrencyCodesFragment) => {
      const symbol = getCurrencySymbolFromCurrencyCode(option.code);
      return {
        value: option.code,
        label: `${option.displayName} (${symbol}, ${option.code})`,
        key: `${option.code}-${uuid()}`,
      };
    }) || [];

  const onCancel = () => {
    setSelectedCurrency(defaultValue);
    onClose();
  };

  const onSave = useCallback(() => {
    if (eventId && selectedCurrency) {
      const currencyFragment = currencyList?.items?.filter((val: RegistryCurrencyCodesFragment) => val.code === selectedCurrency.value)[0];
      if (currencyFragment) {
        handleOnSave(eventId, currencyFragment);
      }
    }
    onClose();
  }, [handleOnSave, onClose, eventId, selectedCurrency, currencyList]);

  return { onCancel, onSave, currencyOptions, selectedCurrency, setSelectedCurrency };
};
